'use client';

import { useEffect, useRef, useCallback } from 'react';
import { useAdMesh } from './useAdMesh';
import {
  createInlineExposureTracker,
  type InlineExposureTrackingParams
} from '../utils/inlineExposureTracker';

export interface UseWeaveAdFormatOptions {
  /** Container ID for the LLM output (where AdMesh links will be detected) */
  llmOutputContainerId: string;

  /** Timeout for link detection (default: 900ms) */
  timeoutMs?: number;

  /** Fallback format if no links detected (default: 'tail') */
  fallbackFormat?: 'product' | 'tail';

  /** Optional callback when AdMesh links are detected */
  onLinksDetected?: (count: number) => void;

  /** Optional callback when no links are detected (fallback should be rendered) */
  onNoLinksDetected?: () => void;

  /** Optional callback on error */
  onError?: (error: Error) => void;

  /** Optional query for fallback API calls (used when no links are detected) */
  query?: string;

  /** Optional message ID for fallback API calls (used when no links are detected) */
  messageId?: string;
}

/**
 * useWeaveAdFormat - Hook for automatic Weave Ad Format handling
 * 
 * Automatically:
 * - Detects AdMesh links in LLM output
 * - Fires exposure tracking for detected links
 * - Adds Ad labels to links
 * - Shows "Why this ad?" tooltip on hover
 * - Renders fallback UI if no links detected
 * 
 * @example
 * ```tsx
 * const { isProcessing, detectedLinksCount } = useWeaveAdFormat({
 *   llmOutputContainerId: 'llm-output-123',
 *   timeoutMs: 1500,
 *   fallbackFormat: 'tail'
 * });
 * ```
 */
export const useWeaveAdFormat = (options: UseWeaveAdFormatOptions) => {
  const { sdk, sessionId } = useAdMesh();
  const processingRef = useRef(false);
  const detectedLinksRef = useRef(0);
  const linksFoundRef = useRef(false);
  const callbackFiredRef = useRef(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const exposureTrackerRef = useRef(createInlineExposureTracker());

  useEffect(() => {
    return () => {
      exposureTrackerRef.current.cleanup();
    };
  }, []);

  const trackInlineExposure = useCallback(
    ({ exposureUrl, recommendationId, linkElement }: InlineExposureTrackingParams) => {
      exposureTrackerRef.current.startTracking({
        exposureUrl,
        recommendationId,
        linkElement,
        sessionId,
        logPrefix: '[useWeaveAdFormat]'
      });
    },
    [sessionId]
  );

  // Phase 1: Enhanced Mutation Silence Detection
  // Track when last mutation occurred for silence detection
  const lastMutationTimeRef = useRef<number>(Date.now());
  // Count consecutive silence checks (requires 2 for confirmation)
  const mutationSilenceCountRef = useRef<number>(0);
  // Store interval timer for cleanup
  const silenceCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Phase 2: Adaptive Debounce Detection
  // Track timestamps of recent mutations to calculate adaptive timeout
  const mutationTimestampsRef = useRef<number[]>([]);
  // Store the calculated adaptive timeout value
  const adaptiveTimeoutRef = useRef<number>(300); // Default to 300ms

  const processWeaveFormat = useCallback(async () => {
    if (!sdk || !sessionId || processingRef.current) {
      return;
    }

    processingRef.current = true;
    detectedLinksRef.current = 0;

    try {
      const container = document.getElementById(options.llmOutputContainerId);
      if (!container) {
        
        return;
      }

      // Get the WeaveResponseProcessor from SDK
      const weaveProcessor = (sdk as any).getWeaveProcessor?.(); // eslint-disable-line @typescript-eslint/no-explicit-any
      if (!weaveProcessor) {
        return;
      }

      // Scan and process AdMesh links in the container
      const detectedLinks = weaveProcessor.scanAndProcessLinks(
        container,
        [], // Recommendations will be fetched from SDK cache
        ({ exposureUrl, recommendationId, linkElement }: InlineExposureTrackingParams) =>
          trackInlineExposure({ exposureUrl, recommendationId, linkElement })
      );

      detectedLinksRef.current = detectedLinks.length;
      const hasLinks = detectedLinks.length > 0;

      // Update the flag indicating whether links were found
      if (hasLinks && !linksFoundRef.current) {
        // Links detected (first time or after streaming)
        linksFoundRef.current = true;
        options.onLinksDetected?.(detectedLinks.length);
        callbackFiredRef.current = true;
      } else if (!hasLinks && linksFoundRef.current) {
        // Links were found before but now they're gone (shouldn't happen in normal flow)
        linksFoundRef.current = false;
        callbackFiredRef.current = false;
      } else if (!hasLinks && !callbackFiredRef.current) {
        // No links found and callback hasn't been fired yet
        // Use enhanced mutation silence detection to wait for streaming to complete
        // This prevents firing the callback too early during streaming

        // Clear existing debounce timer
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }

        // Clear existing silence check interval
        if (silenceCheckIntervalRef.current) {
          clearInterval(silenceCheckIntervalRef.current);
        }

        // Reset silence counter when starting new detection
        mutationSilenceCountRef.current = 0;
        lastMutationTimeRef.current = Date.now();

        // Phase 1 + Phase 2: Enhanced Mutation Silence Detection with Adaptive Timeout
        // Check for mutation silence at adaptive intervals
        // Require 2 consecutive silence checks before declaring streaming complete
        const silenceCheckInterval = Math.max(100, adaptiveTimeoutRef.current / 3); // Check 3x per adaptive timeout

        silenceCheckIntervalRef.current = setInterval(() => {
          const timeSinceLastMutation = Date.now() - lastMutationTimeRef.current;
          const SILENCE_THRESHOLD = adaptiveTimeoutRef.current; // Use adaptive timeout as silence threshold

          if (timeSinceLastMutation >= SILENCE_THRESHOLD) {
            // Mutation silence detected
            mutationSilenceCountRef.current++;

            // Require 2 consecutive silence checks for confirmation
            if (mutationSilenceCountRef.current >= 2) {
              // Clear the interval
              if (silenceCheckIntervalRef.current) {
                clearInterval(silenceCheckIntervalRef.current);
                silenceCheckIntervalRef.current = null;
              }

              // Fire callback if no links were found
              if (!linksFoundRef.current && !callbackFiredRef.current) {
                options.onNoLinksDetected?.();
                callbackFiredRef.current = true;
              }
            }
          } else {
            // Mutations still happening, reset counter
            mutationSilenceCountRef.current = 0;
          }
        }, silenceCheckInterval);

        // Phase 2: Fallback timeout using adaptive timeout
        // Set a maximum timeout to ensure we eventually fire the callback
        // This handles edge cases where mutations might be very infrequent
        const maxFallbackTimeout = Math.max(1500, adaptiveTimeoutRef.current * 5); // At least 1500ms, or 5x adaptive timeout

        debounceTimerRef.current = setTimeout(() => {
          // Clear the silence check interval
          if (silenceCheckIntervalRef.current) {
            clearInterval(silenceCheckIntervalRef.current);
            silenceCheckIntervalRef.current = null;
          }

          // Fire callback if no links were found
          if (!linksFoundRef.current && !callbackFiredRef.current) {
            options.onNoLinksDetected?.();
            callbackFiredRef.current = true;
          }
        }, maxFallbackTimeout);
      } else if (hasLinks && callbackFiredRef.current && !linksFoundRef.current) {
        // Links detected after we initially said "no links found"
        // This happens when links arrive during streaming


        // Clear debounce timer since we found links
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
          debounceTimerRef.current = null;
        }

        linksFoundRef.current = true;
        options.onLinksDetected?.(detectedLinks.length);
        callbackFiredRef.current = true;
      } else {
        
      }
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      options.onError?.(err);
    } finally {
      processingRef.current = false;
    }
  }, [sdk, sessionId, options, trackInlineExposure]);

  // Phase 2: Calculate adaptive debounce timeout based on mutation frequency
  const calculateAdaptiveTimeout = useCallback(() => {
    const timestamps = mutationTimestampsRef.current;

    // Need at least 2 timestamps to calculate interval
    if (timestamps.length < 2) {
      adaptiveTimeoutRef.current = 300; // Default to 300ms
      return;
    }

    // Calculate intervals between consecutive mutations
    const intervals: number[] = [];
    for (let i = 1; i < timestamps.length; i++) {
      intervals.push(timestamps[i] - timestamps[i - 1]);
    }

    // Calculate average interval
    const avgInterval = intervals.reduce((a, b) => a + b, 0) / intervals.length;

    // Adaptive timeout: 3x the average interval, min 100ms, max 1000ms
    const adaptiveTimeout = Math.max(100, Math.min(avgInterval * 3, 1000));

    adaptiveTimeoutRef.current = adaptiveTimeout;
  }, []);

  // Watch for changes in the LLM output container
  useEffect(() => {
    const container = document.getElementById(options.llmOutputContainerId);
    if (!container) {
      return;
    }

    // Initial scan
    processWeaveFormat();

    // Set up MutationObserver for streaming responses
    const observer = new MutationObserver(() => {
      const now = Date.now();

      // Phase 1: Update last mutation time for silence detection
      lastMutationTimeRef.current = now;
      // Reset silence counter since we detected a mutation
      mutationSilenceCountRef.current = 0;

      // Phase 2: Track mutation timestamp for adaptive debounce calculation
      mutationTimestampsRef.current.push(now);
      // Keep only last 10 timestamps to avoid memory bloat
      if (mutationTimestampsRef.current.length > 10) {
        mutationTimestampsRef.current.shift();
      }
      // Recalculate adaptive timeout based on recent mutation pattern
      calculateAdaptiveTimeout();

      processWeaveFormat();
    });

    observer.observe(container, {
      childList: true,
      subtree: true,
      characterData: false
    });

    return () => {
      observer.disconnect();
      // Clean up debounce timer on unmount
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      // Phase 1: Clean up silence check interval on unmount
      if (silenceCheckIntervalRef.current) {
        clearInterval(silenceCheckIntervalRef.current);
      }
    };
  }, [options.llmOutputContainerId, processWeaveFormat, calculateAdaptiveTimeout]);

  return {
    isProcessing: processingRef.current,
    detectedLinksCount: detectedLinksRef.current,
    linksFound: linksFoundRef.current,
    shouldRenderFallback: !linksFoundRef.current && callbackFiredRef.current,
    processWeaveFormat
  };
};

export default useWeaveAdFormat;
